import { apiClient } from "@api/client";
import { request, requestVoid } from "@api/request";
import type { components } from "@api/schema";

type SystemRole = components["schemas"]["SystemRole"];

interface HeldRole {
  id: string;
  name: string;
  systemRole: SystemRole;
}

export interface ClinicUser {
  id: string;
  displayName: string;
  /** 2-digit per-clinic login code. */
  code: string;
  /** `roles[0]`'s label; not a wire field. */
  role: string;
  /** Mirrors brian's `UserOut.roles`: primary first, each grants access on its own. */
  roles: HeldRole[];
  createdAt: string;
}

export const isLeadershipRole = (role: HeldRole): boolean =>
  role.systemRole === "LEADERSHIP";

export const isLeadership = (user: ClinicUser | null): boolean =>
  !!user?.roles.some(isLeadershipRole);

/** Reception: lands on the front desk page instead of the workstation. */
export const isFrontDeskUser = (user: ClinicUser): boolean =>
  user.roles.every((role) => role.systemRole === "OPS");

export const canAccessAdmin = (user: ClinicUser | null): boolean =>
  isLeadership(user);

const toClinicUser = (u: components["schemas"]["UserOut"]): ClinicUser => ({
  id: u.uuid,
  displayName: u.name,
  code: u.code,
  // brian orders `roles` primary-first and every user holds at least one.
  role: u.roles[0].name,
  roles: u.roles.map((r) => ({
    id: r.uuid,
    name: r.name,
    systemRole: r.system_role,
  })),
  createdAt: u.created_at,
});

export const userClient = {
  getCurrent: () =>
    request(() => apiClient.GET("/auth/user", {})).then(toClinicUser),

  list: () =>
    request(() => apiClient.GET("/users", {})).then((users) =>
      users.map(toClinicUser),
    ),

  resetPin: (userId: string, pin: string) =>
    requestVoid(() =>
      apiClient.POST("/users/{user_uuid}/reset-pin", {
        params: { path: { user_uuid: userId } },
        body: { pin },
      }),
    ),

  /** Brian assigns the login code; 409 when the clinic has none left. */
  create: ({
    name,
    pin,
    roleIds,
  }: {
    name: string;
    pin: string;
    roleIds: string[];
  }) =>
    request(() =>
      apiClient.POST("/users", {
        body: { name, pin, clinic_role_uuids: roleIds },
      }),
    ).then(toClinicUser),

  /** Replaces the user's roles, primary first; 409 when the signed-in user
   * would lose their own leadership role. */
  setRoles: ({ userId, roleIds }: { userId: string; roleIds: string[] }) =>
    request(() =>
      apiClient.PUT("/users/{user_uuid}/roles", {
        params: { path: { user_uuid: userId } },
        body: { clinic_role_uuids: roleIds },
      }),
    ).then(toClinicUser),

  /** 409 when the target is the signed-in user. */
  remove: (userId: string) =>
    requestVoid(() =>
      apiClient.DELETE("/users/{user_uuid}", {
        params: { path: { user_uuid: userId } },
      }),
    ),
};
